import React, { useState, useContext, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Box,
  Typography,
  CircularProgress,
  Container
} from "@material-ui/core";
import { 
  Refresh as RefreshIcon, 
  Save as SaveIcon, 
  Schedule as ScheduleIcon, 
  ErrorOutline as ErrorIcon 
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import DoctorSchedulesForm from "../../components/DoctorSchedulesForm";
import { useTitle } from "../../hooks/useTitle";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.default,
  },
  contentWrapper: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  infoBox: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? "rgba(33, 150, 243, 0.1)" : "#e3f2fd",
    borderRadius: theme.shape.borderRadius,
    borderLeft: "4px solid #2196f3",
  },
}));

const MySchedules = () => {
  useTitle("Configuración de Horarios");
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const doctorSchedulesRef = useRef();

  const isDoctor = user && user.isDoctor;

  const handleRefresh = async () => {
    if (doctorSchedulesRef.current) {
      try {
        setLoading(true);
        await doctorSchedulesRef.current.reload();
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (doctorSchedulesRef.current) {
      try {
        setSaving(true);
        await doctorSchedulesRef.current.saveToServer(user.id);
      } catch (err) {
        toastError(err);
      } finally {
        setSaving(false);
      }
    }
  };

  if (!user) return null;

  return (
    <MainContainer>
      <MainHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ScheduleIcon fontSize="large" color="primary" />
            <Title>Mis Horarios</Title>
        </div>
        
        {isDoctor && (
          <MainHeaderButtonsWrapper>
            <div className={classes.headerActions}>
              <Button
                variant="outlined"
                color="default"
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                disabled={loading || saving}
                onClick={handleRefresh}
              >
                Refrescar
              </Button>

              <Button
                color="primary"
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving || loading}
                onClick={handleSave}
              >
                {saving ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          </MainHeaderButtonsWrapper>
        )}
      </MainHeader>

      <div className={classes.mainPaper}>
        <Container maxWidth="lg" disableGutters>
          <Paper className={classes.contentWrapper} variant="outlined">
            {!isDoctor ? (
              <Box display="flex" flexDirection="column" alignItems="center" p={4} textAlign="center">
                <ErrorIcon style={{ fontSize: 60, color: "#9e9e9e", marginBottom: 16 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Acceso Restringido
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Solo los profesionales médicos pueden configurar su disponibilidad horaria.
                </Typography>
              </Box>
            ) : (
              <Box>
                <div className={classes.infoBox}>
                  <Typography variant="body2" color="textPrimary">
                    <strong>Importante:</strong> Define aquí tus franjas horarias de atención semanal. 
                    Recuerda presionar <strong>"Guardar Cambios"</strong> para aplicar las modificaciones.
                  </Typography>
                </div>

                <DoctorSchedulesForm ref={doctorSchedulesRef} doctorId={user.id} />
              </Box>
            )}
          </Paper>
        </Container>
      </div>
    </MainContainer>
  );
};

export default MySchedules;